import { useCallback, useEffect } from 'react';
import { WorldMapProps } from './worldMap.types';
import { ReactComponent as WorldMapSVG } from 'src/assets/world-map.svg';
import { colorChosenCountries } from './worldMap.utils';
import {
  usePanAndZoom,
  UsePanAndZoomHookType,
} from 'src/hooks/usePanAndZoomHook';

const WorldMap: (props: WorldMapProps) => JSX.Element = ({
  chosenCountries,
  chosenCountriesByRegion,
}) => {
  useEffect(() => {
    colorChosenCountries(chosenCountries, chosenCountriesByRegion);
  }, [chosenCountries, chosenCountriesByRegion]);

  const {
    translateX,
    translateY,
    scale,
    worldMapContainerRef,
    worldMapSVGContainerRef,
    onMouseDown,
    onWheel,
    onReset,
    onZoom,
  }: UsePanAndZoomHookType = usePanAndZoom();

  // The wheel event must be handled actively: https://stackoverflow.com/questions/63663025/react-onwheel-handler-cant-preventdefault-because-its-a-passive-event-listenev
  const handleWheel = useCallback((event: WheelEvent): void => {
    if (event.ctrlKey) {
      event.preventDefault();
      onWheel(event);
    }
  }, []);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!(event.target as HTMLElement).className.startsWith('icon')) {
      onMouseDown(event);
    }
  };

  useEffect(() => {
    document
      .getElementById('worldMapButtonOverlay')
      ?.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document
        .getElementById('worldMapButtonOverlay')
        ?.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Special ISOCodes:
  // ALASKA -> US-AK
  // HAWAII -> US-HI
  // WINDWARD ISLANDS -> WWI
  // LEEWARD ISLANDS -> LWI
  // Path without ID -> Galápagos Islands -> Part of Ecuador

  return (
    <div
      aria-hidden="true"
      className="worldMap"
      ref={worldMapContainerRef}
      onMouseDown={handleMouseDown}
    >
      <div
        id="worldMap__svgContainer"
        className="worldMap__svgContainer"
        ref={worldMapSVGContainerRef}
        style={{
          transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
        }}
      >
        <WorldMapSVG />
      </div>

      <div id="worldMapButtonOverlay">
        <div id="worldMap__buttons" className="worldMap__buttons">
          <button className="worldMap__buttons__reset" onClick={onReset}>
            <em className="icon-zuruecksetzen" />
          </button>
          <br />
          <button
            className="worldMap__buttons__zoomIn"
            onClick={() => onZoom(true)}
          >
            <em className="icon-plus" />
          </button>
          <br />
          <button
            className="worldMap__buttons__zoomOut"
            onClick={() => onZoom(false)}
          >
            <em className="icon-minus" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorldMap;
